import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Calendar, Link2, Trash2, Webhook } from 'lucide-react';

import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { Button } from '../../../../components/ui/Button';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { notify } from '../../../../features/feedback/notify';

import {
  createWebhook,
  deleteWebhook,
  fetchWebhooks,
  updateWebhook,
} from '../../../../features/webhooks/webhooksThunks';
import { WEBHOOKS_NOTICE } from '../../../../features/webhooks/webhooksFeedback';
import { selectWebhookById } from '../../../../features/webhooks/webhooksSelectors';
import {
  findWebhookFor,
  getStatusLabel,
  getSubstatusLabel,
  isValidStatusPair,
} from '../../../../features/webhooks/webhooksUtils';

import WebhookForm from './WebhookForm';

const WEBHOOKS_LIST_PATH = '/configuration/webhooks';

/** Remounts the form when the stored webhook changes underneath, not while typing. */
const getFormKey = (webhook) =>
  webhook.id ? `${webhook.id}:${JSON.stringify([webhook.url, webhook.headers, webhook.body])}` : 'new';

/**
 * `/configuration/webhooks/:webhookId` and `/configuration/webhooks/new?status=…&substatus=…`.
 *
 * In "new" mode nothing exists yet: the backend fires a webhook as soon as it is
 * stored, so it is only created once it has its destination. The status pair
 * comes from the "add" dialog and is read-only in both modes.
 *
 * There is no GET by id in the backend, so both modes read the listing.
 */
const WebhookDetail = () => {
  const { webhookId } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !webhookId;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { webhooks, loading, saving, error } = useSelector((state) => state.webhooks);
  const stored = useSelector(selectWebhookById(webhookId));

  const [fetched, setFetched] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Set while this page creates the webhook, so the duplicate guard below does
  // not take the new record for somebody else's.
  const creatingRef = useRef(false);

  const newPair = isNew
    ? { status: searchParams.get('status') || '', substatus: searchParams.get('substatus') || '' }
    : null;
  const webhook = isNew ? newPair : stored;

  useEffect(() => {
    let active = true;
    dispatch(fetchWebhooks()).finally(() => {
      if (active) setFetched(true);
    });
    return () => {
      active = false;
    };
  }, [dispatch]);

  const newStatus = newPair?.status;
  const newSubstatus = newPair?.substatus;

  // "new" only for a valid pair nobody has yet: anything else goes back to the
  // listing, and a pair already taken opens its webhook.
  useEffect(() => {
    if (!isNew) return;
    if (!isValidStatusPair(newStatus, newSubstatus)) {
      navigate(WEBHOOKS_LIST_PATH, { replace: true });
      return;
    }
    if (!fetched || creatingRef.current) return;

    const existing = findWebhookFor(webhooks, newStatus, newSubstatus);
    if (existing) {
      dispatch(notify.error(
        `Ya tienes un webhook para ${getStatusLabel(newStatus)} / ${getSubstatusLabel(newStatus, newSubstatus)}.`,
        { testId: WEBHOOKS_NOTICE }
      ));
      navigate(`${WEBHOOKS_LIST_PATH}/${existing.id}`, { replace: true });
    }
  }, [isNew, newStatus, newSubstatus, fetched, webhooks, navigate, dispatch]);

  const handleInvalid = useCallback(
    (message) => dispatch(notify.error(message, { testId: WEBHOOKS_NOTICE })),
    [dispatch]
  );

  const handleSubmit = async (payload) => {
    if (isNew) {
      creatingRef.current = true;
      const action = await dispatch(createWebhook(payload));
      if (action.meta.requestStatus !== 'fulfilled') {
        creatingRef.current = false;
        return;
      }
      navigate(`${WEBHOOKS_LIST_PATH}/${action.payload.id}`, { replace: true });
      return;
    }

    dispatch(updateWebhook({ id: stored.id, payload }));
  };

  const handleDelete = async () => {
    setDeleting(true);
    const action = await dispatch(deleteWebhook(stored.id));
    setDeleting(false);
    setIsDeleteOpen(false);

    if (action.meta.requestStatus === 'fulfilled') navigate(WEBHOOKS_LIST_PATH);
  };

  if (!webhook) {
    // A retry waits like the first load: the error is cleared as it starts,
    // and until the list answers "not found" would be a guess.
    if (!fetched || loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-ninja-green border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    return (
      <div data-testid="webhook-detail-page" className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-border">
        <ErrorState
          title={error ? 'No se pudieron cargar los webhooks' : 'No encontramos este webhook'}
          description={error || 'Puede que se haya eliminado. Vuelve al listado para ver tus webhooks.'}
          onRetry={error ? () => dispatch(fetchWebhooks()) : undefined}
        >
          <Button variant="ghost" onClick={() => navigate(WEBHOOKS_LIST_PATH)}>
            Volver a Webhooks
          </Button>
        </ErrorState>
      </div>
    );
  }

  const statusLabel = getStatusLabel(webhook.status);
  const substatusLabel = getSubstatusLabel(webhook.status, webhook.substatus);
  const pairLabel = `${statusLabel} / ${substatusLabel}`;

  return (
    <div
      data-testid="webhook-detail-page"
      data-status={webhook.status}
      data-substatus={webhook.substatus}
      className="space-y-8 animate-in fade-in duration-300"
    >
      <DetailBreadcrumb
        backLabel="Volver a Webhooks"
        to={WEBHOOKS_LIST_PATH}
        current={isNew ? `Nuevo webhook · ${pairLabel}` : pairLabel}
      />

      <DetailHero
        testId="webhook-detail-hero"
        media={<Webhook size={40} strokeWidth={1.5} />}
        title={statusLabel}
        badges={
          <>
            <span className="px-2.5 py-1 rounded-full text-xs font-bold border bg-ninja-greenLight dark:bg-ninja-green/20 text-ninja-green border-ninja-green/20">
              {substatusLabel}
            </span>
            {isNew && (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold border bg-ninja-gray dark:bg-[#2d2d2d] text-text-secondary border-border">
                Nuevo
              </span>
            )}
          </>
        }
        meta={
          <>
            <span>Se envía solo cuando el envío queda exactamente en este estado y subestado.</span>
            {!isNew && (
              <>
                <span className="flex items-center gap-1.5 min-w-0">
                  <Link2 size={14} className="flex-shrink-0" />
                  <span className="truncate max-w-[280px]" title={webhook.url}>{webhook.url}</span>
                </span>
                {webhook.created_at && (
                  <span className="flex items-center gap-1.5">
                    <Calendar size={14} /> Creado el{' '}
                    {new Date(webhook.created_at).toLocaleDateString('es-CL')}
                  </span>
                )}
              </>
            )}
          </>
        }
        actions={
          !isNew && (
            <Button
              variant="danger"
              icon={Trash2}
              onClick={() => setIsDeleteOpen(true)}
              data-testid="webhook-delete"
            >
              Eliminar
            </Button>
          )
        }
      />

      <section className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 sm:p-8 shadow-sm border border-border">
        <WebhookForm
          key={getFormKey(webhook)}
          initialWebhook={webhook}
          isEditing={!isNew}
          saving={saving}
          onSubmit={handleSubmit}
          onInvalid={handleInvalid}
        />
      </section>

      {!isNew && (
        <ConfirmDialog
          isOpen={isDeleteOpen}
          onClose={() => !deleting && setIsDeleteOpen(false)}
          onConfirm={handleDelete}
          title="Eliminar webhook"
          message={`Se elimina el webhook de ${pairLabel}. Tu sistema dejará de recibir ese aviso.`}
          loading={deleting}
          testId="webhook-delete-dialog"
        />
      )}
    </div>
  );
};

export default memo(WebhookDetail);
